import * as React from "react";
import { observer } from "mobx-react";

import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { UitkSpacing } from "uitk-react-spacing";

import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { LobTitle } from "components/shared/LobTitle/LobTitle";
import { LobSubtitle } from "components/shared/LobSubtitle/LobSubtitle";
import { Travelers } from "components/shared/Travelers/Travelers";
import { TravelersTriggerTypes } from "components/shared/TravelersField/typings";
import { CarSubLOBs } from "stores/wizard/state/typings";

import { CarsFormView } from "../components/CarsFormView/CarsFormView";
import {
  GroundTransportationPWAViewProps,
  GroundTransportPWAView,
} from "../components/GroundTransportPWA/GroundTransportPWAView";
import { WizardCarPWAViewProps, WizardCarPWAFTNodeOptions } from "../typings";
import { locKeys } from "../components/l10n";
import { CarTypeDropdown } from "../components/vendorOptions/CarTypeDropdown";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";

import { normalizeOptionsData } from "src/stores/FlexTemplateStore";

export const WizardCarPWAView: React.FunctionComponent<WizardCarPWAViewProps> = observer(
  ({
    context,
    isAARPChecked,
    isDriverAgeInRange,
    isDiffDropOff,
    onAARPChange,
    onDifferentDropOffSelected,
    onDriverAgeInRange,
    onTabOptionSelected,
    selectedTab,
    setIsDifferentDropOff,
    toggleBookRoundtrip,
    wizardState,
    showGTOnly = false,
    pageHeading,
    setInputsRefs,
    getLocError,
    flexModuleModelStore,
    flexTemplateNode,
  }) => {
    const { formatText } = useLocalization();
    const {
      globalWizardState,
      carWizardState,
      carWizardState: { config: carConfig },
      carWizardState: {
        config: { gtConfig: gtConfig },
      },
    } = wizardState;
    const globalConfig = globalWizardState.config;
    const includeGT = carConfig.includeGT;
    const actionForm =
      selectedTab === CarSubLOBs.RENTAL
        ? carConfig.isCarTrawler
          ? carConfig.form.action.replace("{locale}", context.locale.substr(0, 2))
          : carConfig.form.action
        : gtConfig.form.action;

    const onTabSelect = (clickedTab: CarSubLOBs) => {
      onTabOptionSelected(clickedTab);
    };

    const model = flexModuleModelStore.getModel(flexTemplateNode.id);
    const options = normalizeOptionsData(flexTemplateNode, model) as WizardCarPWAFTNodeOptions;

    const gtProps: Omit<GroundTransportationPWAViewProps, "routeStore"> = {
      carWizardState,
      toggleBookRoundtrip,
      setInputsRefs,
      getLocError,
      gtConfig,
      globalConfig,
    };
    const gtView = <GroundTransportPWAView {...gtProps} />;

    const noNavigationItems = () => (
      <Viewport>
        <ViewSmall />
        <ViewMedium>
          <>
            {!carConfig.isBiasedWizardEmpty && !carConfig.carType && (
              <UitkLayoutFlex className="carTypeSelect">
                <UitkLayoutFlexItem>
                  <CarTypeDropdown carWizardState={carWizardState} />
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            )}

            {carWizardState.subLOBState === CarSubLOBs.GROUND_TRANSPORTATION && (
              <UitkLayoutFlex alignItems="center" space="six" className="all-x-padding-six">
                <UitkLayoutFlexItem>
                  <Travelers
                    lobState={carWizardState}
                    colSpan={{ medium: 12 }}
                    config={carWizardState.groundTransportationLocationState.config.travelers}
                    triggerType={TravelersTriggerTypes.LINK_BUTTON}
                  />
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            )}
          </>
        </ViewMedium>
      </Viewport>
    );

    const carView = (
      <CarsFormView
        setInputsRefs={setInputsRefs}
        carWizardState={carWizardState}
        carConfig={carConfig}
        onDriverAgeInRange={onDriverAgeInRange}
        onDifferentDropOffSelected={onDifferentDropOffSelected}
        setIsDifferentDropOff={setIsDifferentDropOff}
        isDriverAgeInRange={isDriverAgeInRange}
        isDiffDropOff={isDiffDropOff}
        getLocError={getLocError}
        onAARPChange={onAARPChange}
        isAARPChecked={isAARPChecked}
        globalConfig={globalConfig}
      />
    );

    return (
      <form
        noValidate={true}
        action={showGTOnly ? gtConfig?.form?.action : actionForm}
        autoComplete="off"
        className="WizardCarPWA"
        id={carConfig.elementId}
        data-fm={options.fmId}
        data-fm-title-id={options.fmTitleId}
        data-testid="wizard"
        onSubmit={carWizardState.submit}
      >
        {showGTOnly ? (
          gtView
        ) : (
          <>
            <LobTitle lobState={carWizardState} customTitle={pageHeading} />
            <LobSubtitle lobState={carWizardState} />
            {includeGT ? (
              <UitkSpacing margin={{ blockstart: "three" }}>
                <UitkExperimentalButtonTabs
                  selectedTab={carWizardState.subLOBState}
                  tabsType={UitkTabsType.NATURAL}
                  onTabSelect={onTabSelect}
                  noNavigationItems={noNavigationItems}
                  tabAnchorType={UitkTabAnchorType.BUTTON}
                  className="carTypeList"
                >
                  <UitkTab
                    name={CarSubLOBs.RENTAL}
                    displayName={formatText(locKeys.carTabTitle)}
                    id="Rental-cars-transportation"
                    targetURI="carsForm"
                  >
                    {carWizardState.subLOBState === CarSubLOBs.RENTAL && carView}
                  </UitkTab>
                  <UitkTab
                    name={CarSubLOBs.GROUND_TRANSPORTATION}
                    displayName={formatText(locKeys.gtTabTitle)}
                    id="Airport-transportation"
                    targetURI="gtForm"
                  >
                    {carWizardState.subLOBState === CarSubLOBs.GROUND_TRANSPORTATION && gtView}
                  </UitkTab>
                </UitkExperimentalButtonTabs>
              </UitkSpacing>
            ) : (
              carView
            )}
          </>
        )}
      </form>
    );
  }
);

export default WizardCarPWAView;
